import {
  CONCIERGE_MEMORY_KEY,
  type ConciergeMemory,
  memorySelectionSearch,
  parseConciergeMemory,
} from "./domain/conciergeMemory";
import { parseDaypart } from "./domain/concierge";

const memory = readMemory();
applyRememberedSelection(memory);

if (typeof window !== "undefined") {
  const nativeReplaceState = window.history.replaceState.bind(window.history);
  window.history.replaceState = ((data: unknown, unused: string, url?: string | URL | null) => {
    nativeReplaceState(data, unused, url);
    saveSelectionFromUrl();
  }) as History["replaceState"];

  const mountControls = () => {
    mountMemoryPrivacyControl();
    mountMemoryControls();
  };
  const observer = new MutationObserver(mountControls);
  observer.observe(document.documentElement, { childList: true, subtree: true });
  window.addEventListener("DOMContentLoaded", mountControls);
}

function readMemory(): ConciergeMemory | undefined {
  try {
    return parseConciergeMemory(window.localStorage.getItem(CONCIERGE_MEMORY_KEY));
  } catch {
    return undefined;
  }
}

function applyRememberedSelection(stored: ConciergeMemory | undefined): void {
  if (!stored || window.location.search) return;
  const search = memorySelectionSearch(stored);
  if (search) window.history.replaceState(null, "", `${window.location.pathname}?${search}`);
}

function mountMemoryPrivacyControl(): void {
  const host = document.querySelector<HTMLElement>("main.page");
  const stored = readMemory();
  if (!host || !stored || host.querySelector("[data-concierge-memory-privacy]")) return;

  const panel = document.createElement("section");
  panel.className = "concierge-memory concierge-memory--privacy";
  panel.dataset.conciergeMemoryPrivacy = "true";
  panel.innerHTML = `
    <div>
      <strong>Gespeicherte Angaben auf diesem Gerät</strong>
      <span>Du kannst deine lokal gespeicherten Kontakt- und Auswahldaten jederzeit löschen – unabhängig davon, ob ein Termin verfügbar ist.</span>
    </div>
    <button type="button" class="text-action">Gespeicherte Angaben löschen</button>
  `;

  const firstBookingSection = host.querySelector(".section");
  if (firstBookingSection) firstBookingSection.before(panel);
  else host.prepend(panel);

  panel.querySelector<HTMLButtonElement>("button")?.addEventListener("click", () => {
    clearMemory();
    panel.remove();
    const formToggle = document.querySelector<HTMLInputElement>(
      '[data-concierge-memory] input[type="checkbox"]',
    );
    if (formToggle) formToggle.checked = false;
  });
}

function mountMemoryControls(): void {
  const form = document.querySelector<HTMLFormElement>("#angaben form");
  if (!form || form.querySelector("[data-concierge-memory]")) return;

  const stored = readMemory();
  const panel = document.createElement("section");
  panel.className = "concierge-memory";
  panel.dataset.conciergeMemory = "true";
  panel.innerHTML = `
    <div>
      <strong>Beim nächsten Mal schneller</strong>
      <span>Kontaktdaten und bevorzugte Auswahl nur auf diesem Gerät merken. Anliegen und Einwilligung werden nie gespeichert.</span>
    </div>
    <label>
      <input type="checkbox" ${stored ? "checked" : ""} />
      <span>Auf diesem Gerät merken</span>
    </label>
    <button type="button" class="text-action">Gespeicherte Angaben löschen</button>
  `;

  form.prepend(panel);
  const toggle = panel.querySelector<HTMLInputElement>('input[type="checkbox"]');
  const clear = panel.querySelector<HTMLButtonElement>("button");
  if (!toggle || !clear) return;

  if (stored) prefillContact(form, stored);

  toggle.addEventListener("change", () => {
    if (toggle.checked) {
      saveContact(form);
      mountMemoryPrivacyControl();
    } else {
      clearMemory();
      removeMemoryPrivacyControl();
    }
  });

  clear.addEventListener("click", () => {
    clearMemory();
    removeMemoryPrivacyControl();
    toggle.checked = false;
    clear.textContent = "Gespeicherte Angaben gelöscht";
    window.setTimeout(() => {
      clear.textContent = "Gespeicherte Angaben löschen";
    }, 1800);
  });

  form.addEventListener("input", () => {
    if (toggle.checked) saveContact(form);
  });
}

function removeMemoryPrivacyControl(): void {
  document.querySelector("[data-concierge-memory-privacy]")?.remove();
}

function prefillContact(form: HTMLFormElement, stored: ConciergeMemory): void {
  const fields = Array.from(form.querySelectorAll<HTMLInputElement>("input"));
  setReactInput(fields.find((field) => field.type === "text"), stored.contact.firstName);
  setReactInput(fields.filter((field) => field.type === "text")[1], stored.contact.lastName);
  setReactInput(fields.find((field) => field.type === "email"), stored.contact.email);
  setReactInput(fields.find((field) => field.type === "tel"), stored.contact.phone);
}

function setReactInput(field: HTMLInputElement | undefined, value: string): void {
  if (!field || !value || field.value) return;
  const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, "value")?.set;
  setter?.call(field, value);
  field.dispatchEvent(new Event("input", { bubbles: true }));
}

function saveContact(form: HTMLFormElement): void {
  const fields = Array.from(form.querySelectorAll<HTMLInputElement>("input"));
  const texts = fields.filter((field) => field.type === "text");
  const previous = readMemory();
  const next: ConciergeMemory = {
    enabled: true,
    contact: {
      firstName: texts[0]?.value.trim() ?? "",
      lastName: texts[1]?.value.trim() ?? "",
      email: fields.find((field) => field.type === "email")?.value.trim() ?? "",
      phone: fields.find((field) => field.type === "tel")?.value.trim() ?? "",
    },
    selection: selectionFromUrl() ?? previous?.selection,
    updatedAt: new Date().toISOString(),
  };

  try {
    window.localStorage.setItem(CONCIERGE_MEMORY_KEY, JSON.stringify(next));
  } catch {
    // The booking flow must remain usable when browser storage is blocked.
  }
}

function saveSelectionFromUrl(): void {
  const stored = readMemory();
  if (!stored) return;
  const selection = selectionFromUrl();
  if (!selection) return;

  try {
    window.localStorage.setItem(
      CONCIERGE_MEMORY_KEY,
      JSON.stringify({ ...stored, selection, updatedAt: new Date().toISOString() }),
    );
  } catch {
    // Ignore unavailable browser storage.
  }
}

function selectionFromUrl(): ConciergeMemory["selection"] | undefined {
  const params = new URLSearchParams(window.location.search);
  const offerSlug = params.get("angebot");
  const variantSlug = params.get("dauer");
  const locationSlug = params.get("ort");
  if (!offerSlug || !variantSlug || !locationSlug) return undefined;
  return {
    offerSlug,
    variantSlug,
    locationSlug,
    daypart: parseDaypart(params.get("tageszeit")),
  };
}

function clearMemory(): void {
  try {
    window.localStorage.removeItem(CONCIERGE_MEMORY_KEY);
  } catch {
    // Ignore unavailable browser storage.
  }
}
